import { useRef, useEffect, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AudioClip } from '@/types/timeline.ts';
import { useAudioStore } from '@/store/audioStore.ts';
import { useUndoStore } from '@/store/undoStore.ts';
import { useProjectStore } from '@/store/projectStore.ts';
import { usePlaybackStore } from '@/store/playbackStore.ts';
import { getClipAbsoluteStart, resolveClipPlacement } from '@/store/projectDerived.ts';
import { LANE_COLORS } from '@/audio/audioConstants.ts';
import styles from './AudioClipBar.module.css';

type DragMode = 'move' | 'trim-left' | 'trim-right';

interface DragState {
  mode: DragMode;
  startX: number;
  startY: number;
  origStartTime: number;
  origDuration: number;
  origLane: number;
  origBufferOffset: number;
}

interface AudioClipBarProps {
  clip: AudioClip;
  pixelsPerSecond: number;
  laneHeight: number;
  viewOffset?: number;
  viewEnd?: number;
  expanded?: boolean;
  isSelected?: boolean;
  onSelect?: (id: number) => void;
}

const IS_COARSE = typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches;
const TRIM_HANDLE_WIDTH = IS_COARSE ? 20 : 8;
const DEAD_ZONE = IS_COARSE ? 8 : 3;
const LONG_PRESS_MS = 300;
const MIN_DURATION = 0.1;
const SNAP = 0.1;

function snap(v: number): number {
  return Math.round(v / SNAP) * SNAP;
}

export function AudioClipBar({ clip, pixelsPerSecond, laneHeight, viewOffset = 0, viewEnd = Infinity, expanded = false, isSelected = false, onSelect }: AudioClipBarProps) {
  const { t } = useTranslation('timeline');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dragRef = useRef<DragState | null>(null);
  const didDragRef = useRef(false);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressReadyRef = useRef(false);
  const pendingPointerRef = useRef<{ e: React.PointerEvent; mode: DragMode; el: HTMLElement } | null>(null);
  const [longPressActive, setLongPressActive] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const updateAudioClip = useAudioStore(s => s.updateAudioClip);
  const removeAudioClip = useAudioStore(s => s.removeAudioClip);
  const _pushSnapshot = useUndoStore(s => s.pushSnapshot);
  const readOnly = useProjectStore(s => s.readOnly);
  const project = useProjectStore(s => s.project);
  const isPlaying = usePlaybackStore(s => s.isPlaying);
  const locked = readOnly || isPlaying;

  const absoluteStart = project ? getClipAbsoluteStart(clip, project) : 0;
  const width = clip.duration * pixelsPerSecond;
  const left = (absoluteStart - viewOffset) * pixelsPerSecond;
  const top = expanded ? clip.lane * laneHeight : 0;

  // Draw waveform when peaks change or dimensions change
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !clip.peaks) return;

    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, w, h);

    const peaks = clip.peaks;
    const totalPeaks = peaks.length;
    const bufferOffset = clip.bufferOffset ?? 0;
    const originalDuration = clip.audioBuffer?.duration ?? clip.duration + bufferOffset;

    // Calculate the visible portion of peaks based on bufferOffset
    const startFrac = originalDuration > 0 ? bufferOffset / originalDuration : 0;
    const endFrac = originalDuration > 0 ? (bufferOffset + clip.duration) / originalDuration : 1;
    const startIdx = Math.floor(startFrac * totalPeaks);
    const endIdx = Math.ceil(endFrac * totalPeaks);
    const visiblePeaks = endIdx - startIdx;

    const barWidth = Math.max(1, w / Math.max(visiblePeaks, 1));
    const midY = h / 2;
    const color = clip.isMuted ? '#555' : clip.color;

    ctx.fillStyle = color;
    ctx.globalAlpha = clip.isMuted ? 0.4 : 0.7;

    for (let i = 0; i < visiblePeaks; i++) {
      const peakIdx = startIdx + i;
      if (peakIdx >= totalPeaks) break;
      const x = (i / visiblePeaks) * w;
      const peakH = peaks[peakIdx] * midY * 0.9;
      ctx.fillRect(x, midY - peakH, barWidth, peakH * 2);
    }

    // Center line
    ctx.globalAlpha = 0.3;
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, midY, w, 0.5);
  }, [clip.peaks, clip.color, clip.isMuted, width, clip.bufferOffset, clip.duration, clip.audioBuffer]);

  // Clean up long-press timer on unmount
  useEffect(() => {
    return () => {
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    };
  }, []);

  // Determine drag mode from pointer position
  const getDragMode = useCallback((e: React.PointerEvent): DragMode => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const relX = e.clientX - rect.left;
    if (relX < TRIM_HANDLE_WIDTH) return 'trim-left';
    if (relX > rect.width - TRIM_HANDLE_WIDTH) return 'trim-right';
    return 'move';
  }, []);

  /** Start actual drag — called immediately on desktop, after long-press on touch */
  const initiateDrag = useCallback((startX: number, startY: number, mode: DragMode, el: HTMLElement, pointerId: number) => {
    _pushSnapshot();
    didDragRef.current = false;
    longPressReadyRef.current = true;
    setIsDragging(true);

    const proj = useProjectStore.getState().project;
    const absStart = proj ? getClipAbsoluteStart(clip, proj) : 0;
    dragRef.current = {
      mode,
      startX,
      startY,
      origStartTime: absStart,
      origDuration: clip.duration,
      origLane: clip.lane,
      origBufferOffset: clip.bufferOffset ?? 0,
    };
    el.setPointerCapture(pointerId);

    // Haptic feedback on touch
    if (IS_COARSE && mode === 'move') {
      try { navigator.vibrate?.(10); } catch { /* ok */ }
    }
  }, [clip.shotId, clip.shotOffset, clip.duration, clip.lane, clip.bufferOffset, _pushSnapshot]);

  const cancelLongPress = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    setLongPressActive(false);
    pendingPointerRef.current = null;
  }, []);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    // Don't initiate drag from volume controls, delete button, or info button
    if ((e.target as HTMLElement).closest(`.${styles.volumeControls}`) ||
        (e.target as HTMLElement).closest(`.${styles.deleteBtn}`) ||
        (e.target as HTMLElement).closest(`.${styles.infoBtn}`)) return;

    e.stopPropagation();
    e.preventDefault();
    didDragRef.current = false;
    longPressReadyRef.current = false;

    const mode = getDragMode(e);
    const el = e.currentTarget as HTMLElement;

    // Trim handles work immediately on all platforms (no long-press gate)
    if (mode !== 'move' || !IS_COARSE) {
      initiateDrag(e.clientX, e.clientY, mode, el, e.pointerId);
      return;
    }

    // Coarse pointer + move: require long-press
    pendingPointerRef.current = { e, mode, el };
    setLongPressActive(true);

    longPressTimerRef.current = setTimeout(() => {
      longPressTimerRef.current = null;
      const pending = pendingPointerRef.current;
      if (pending) {
        initiateDrag(pending.e.clientX, pending.e.clientY, pending.mode, pending.el, pending.e.pointerId);
        setLongPressActive(false);
        pendingPointerRef.current = null;
      }
    }, LONG_PRESS_MS);
  }, [getDragMode, initiateDrag]);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    // During long-press wait: cancel if moved too far (scroll/swipe intent)
    if (pendingPointerRef.current && !longPressReadyRef.current) {
      const pending = pendingPointerRef.current;
      const dx = e.clientX - pending.e.clientX;
      const dy = e.clientY - pending.e.clientY;
      if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
        cancelLongPress();
      }
      return;
    }

    const drag = dragRef.current;
    if (!drag) return;

    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    if (!didDragRef.current && (Math.abs(dx) > DEAD_ZONE || Math.abs(dy) > DEAD_ZONE)) {
      didDragRef.current = true;
    }

    const dSeconds = dx / pixelsPerSecond;

    if (drag.mode === 'move') {
      const proj = useProjectStore.getState().project;
      const maxStart = viewEnd - clip.duration;  // keep clip end within timeline
      const newAbsoluteStart = snap(Math.max(viewOffset, Math.min(maxStart, drag.origStartTime + dSeconds)));
      const placement = proj ? resolveClipPlacement(newAbsoluteStart, proj) : { shotId: clip.shotId, shotOffset: newAbsoluteStart };
      const updates: Partial<AudioClip> = { shotId: placement.shotId, shotOffset: placement.shotOffset };

      // Vertical drag switches lane when expanded
      if (expanded) {
        const laneDelta = Math.round(dy / laneHeight);
        const newLane = Math.max(0, Math.min(3, drag.origLane + laneDelta));
        updates.lane = newLane;
        updates.color = LANE_COLORS[newLane];
      }

      updateAudioClip(clip.id, updates, { pushUndo: false });
    } else if (drag.mode === 'trim-left') {
      // Trim left: adjust shotId/shotOffset, duration, and bufferOffset
      const proj = useProjectStore.getState().project;
      const maxTrim = drag.origDuration - MIN_DURATION;
      const clampedD = Math.max(-drag.origBufferOffset, Math.min(maxTrim, dSeconds));
      const snapped = snap(clampedD);
      const newAbsoluteStart = drag.origStartTime + snapped;
      const placement = proj ? resolveClipPlacement(newAbsoluteStart, proj) : { shotId: clip.shotId, shotOffset: newAbsoluteStart };
      updateAudioClip(clip.id, {
        shotId: placement.shotId,
        shotOffset: placement.shotOffset,
        duration: drag.origDuration - snapped,
        bufferOffset: drag.origBufferOffset + snapped,
      }, { pushUndo: false });
    } else if (drag.mode === 'trim-right') {
      // Trim right: adjust duration only
      const newDuration = snap(Math.max(MIN_DURATION, drag.origDuration + dSeconds));
      updateAudioClip(clip.id, { duration: newDuration }, { pushUndo: false });
    }
  }, [clip.id, clip.duration, pixelsPerSecond, viewOffset, viewEnd, expanded, laneHeight, updateAudioClip, cancelLongPress]);

  const onPointerUp = useCallback(() => {
    cancelLongPress();
    if (!didDragRef.current && onSelect) {
      onSelect(clip.id);
    }
    dragRef.current = null;
    longPressReadyRef.current = false;
    setIsDragging(false);
  }, [clip.id, onSelect, cancelLongPress]);

  // Volume slider handler
  const onVolumeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    updateAudioClip(clip.id, { volume: Number(e.target.value) / 100 }, { pushUndo: false });
  }, [clip.id, updateAudioClip]);

  const onVolumePointerDown = useCallback((e: React.PointerEvent) => {
    e.stopPropagation();
    _pushSnapshot();
  }, [_pushSnapshot]);

  // Rename clip via prompt
  const onInfoClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    const newLabel = window.prompt(t('renameAudioClipPrompt'), clip.label);
    if (newLabel !== null && newLabel !== clip.label) {
      _pushSnapshot();
      updateAudioClip(clip.id, { label: newLabel }, { pushUndo: false });
    }
  }, [clip.id, clip.label, _pushSnapshot, updateAudioClip, t]);

  // Delete clip (no window.confirm — WKWebView suppresses it; undo is available)
  const onDeleteClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    removeAudioClip(clip.id);
  }, [clip.id, removeAudioClip]);

  const volPct = `${Math.round(clip.volume * 100)}%`;

  return (
    <div
      className={`${styles.clipBar} ${clip.isMuted ? styles.muted : ''} ${isSelected ? styles.selected : ''} ${longPressActive ? styles.longPressActive : ''} ${isDragging ? styles.dragging : ''}`}
      style={{
        left,
        top,
        width: Math.max(width, 4),
        height: laneHeight - 4,
        borderColor: clip.isMuted ? '#555' : clip.color,
      }}
      onPointerDown={locked ? undefined : onPointerDown}
      onPointerMove={locked ? undefined : onPointerMove}
      onPointerUp={locked ? undefined : onPointerUp}
      onPointerCancel={locked ? undefined : onPointerUp}
    >
      {/* Trim handles */}
      <div className={styles.trimHandleLeft} />
      <div className={styles.trimHandleRight} />

      <canvas ref={canvasRef} className={styles.waveform} />
      <div className={styles.label}>{clip.label}</div>

      {/* Info/rename button — visible on hover, hidden in review mode */}
      {!locked && (
        <button
          className={styles.infoBtn}
          onClick={onInfoClick}
          onPointerDown={(e) => e.stopPropagation()}
          onPointerUp={(e) => e.stopPropagation()}
          title={t('renameClipTitle')}
        >
          i
        </button>
      )}

      {/* Delete button — visible on hover, hidden in review mode */}
      {!locked && (
        <button
          className={styles.deleteBtn}
          onClick={onDeleteClick}
          onPointerDown={(e) => e.stopPropagation()}
          onPointerUp={(e) => e.stopPropagation()}
          title={t('deleteClipTitle')}
        >
          &times;
        </button>
      )}

      {/* Volume controls — visible on hover, hidden in review mode */}
      {!locked && (
        <div className={styles.volumeControls} style={{ '--vol-pct': volPct } as React.CSSProperties}>
          <input
            type="range"
            className={styles.volumeSlider}
            min={0}
            max={100}
            step={1}
            value={Math.round(clip.volume * 100)}
            onChange={onVolumeChange}
            onPointerDown={onVolumePointerDown}
            title={t('volumeTitle', { percent: Math.round(clip.volume * 100) })}
          />
        </div>
      )}
    </div>
  );
}
